import { type ColumnDef, getCoreRowModel } from '@tanstack/react-table'
import { useId, useMemo } from 'react'
import { useDataTable } from '../../hooks/use-data-table'
import { DataTable } from '../ui/data-table'
import { EmptyState } from '../ui/empty-state'
import { Section, SectionBody, SectionHeader, SectionTitle } from '../ui/section'
import { ImportStatusBadge } from './import-status-badge'

/** One import as the history lists it. `report` is read for its result counts only. */
export type ImportHistoryItem = {
  id: string
  created_at: string | null
  original_name: string
  uploaded_by: string | null
  door: string
  status: string
  report: { sections: ReadonlyArray<{ created: number; updated: number }> } | null
}

export type ImportHistoryLabels = {
  title: string
  empty: string
  emptyDescription: string
  uploadedAt: string
  file: string
  uploadedBy: string
  status: string
  result: string
  open: string
}

export type ImportHistoryProps = {
  items: ImportHistoryItem[]
  /** The import whose preview or result is open; its row is marked. */
  selectedId: string | null
  onOpen: (id: string) => void
  labels: ImportHistoryLabels
  formatDate: (value: string) => string
  formatResult: (totals: { created: number; updated: number }) => string
  /** Who or what the import came from, in the caller's words. */
  doorLabel: (door: string) => string
  statusLabel: (status: string) => string
}

/**
 * The imports of one company, newest first. A row's Open action shows its
 * preview or result. Texts and formatters come in as props, so this component
 * holds no app wording.
 */
export function ImportHistory({
  items,
  selectedId,
  onOpen,
  labels,
  formatDate,
  formatResult,
  doorLabel,
  statusLabel,
}: ImportHistoryProps) {
  const headingId = useId()
  const columns = useMemo<ColumnDef<ImportHistoryItem>[]>(
    () => [
      {
        id: 'uploadedAt',
        header: labels.uploadedAt,
        cell: ({ row }) => (row.original.created_at ? formatDate(row.original.created_at) : ''),
        meta: { phone: 'meta', phoneLabel: labels.uploadedAt },
      },
      {
        id: 'file',
        header: labels.file,
        cell: ({ row }) => (
          <span className="block max-w-[220px] truncate">{row.original.original_name}</span>
        ),
        meta: { phone: 'primary' },
      },
      {
        id: 'uploadedBy',
        header: labels.uploadedBy,
        cell: ({ row }) => (
          <>
            <span className="block whitespace-normal break-words">{row.original.uploaded_by}</span>
            <span className="text-small whitespace-normal text-muted-foreground">
              {doorLabel(row.original.door)}
            </span>
          </>
        ),
        meta: { phone: 'secondary' },
      },
      {
        id: 'status',
        header: labels.status,
        cell: ({ row }) => (
          <ImportStatusBadge
            status={row.original.status}
            label={statusLabel(row.original.status)}
            data-testid="data-import-status"
          />
        ),
        meta: { phone: 'trailing' },
      },
      {
        id: 'result',
        header: labels.result,
        cell: ({ row }) => {
          const { status, report } = row.original
          if (status !== 'applied' || !report) return ''
          return formatResult(
            report.sections.reduce(
              (sum, section) => ({
                created: sum.created + section.created,
                updated: sum.updated + section.updated,
              }),
              { created: 0, updated: 0 },
            ),
          )
        },
        meta: { phone: 'meta', phoneLabel: labels.result },
      },
      {
        id: 'open',
        header: () => <span className="sr-only">{labels.open}</span>,
        cell: () => null,
        meta: {
          phone: 'actions',
          actions: (row) => [
            {
              label: labels.open,
              // Every row's button reads "Open"; the name says which import it opens.
              ariaLabel: [
                labels.open,
                row.original.original_name,
                row.original.created_at ? formatDate(row.original.created_at) : null,
              ]
                .filter(Boolean)
                .join(' · '),
              onSelect: () => onOpen(row.original.id),
              primary: true,
            },
          ],
        },
      },
    ],
    [labels, formatDate, formatResult, doorLabel, statusLabel, onOpen],
  )

  const table = useDataTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row.id,
    state: { rowSelection: selectedId === null ? {} : { [selectedId]: true } },
  })

  return (
    <Section aria-labelledby={headingId}>
      <SectionHeader>
        <SectionTitle id={headingId}>{labels.title}</SectionTitle>
      </SectionHeader>
      <SectionBody>
        {items.length === 0 ? (
          <EmptyState title={labels.empty} description={labels.emptyDescription} />
        ) : (
          <DataTable
            table={table}
            phoneLayout="rows"
            data-testid="data-import-history"
            rowProps={(row) => ({ 'data-testid': `data-import-row-${row.original.id}` })}
          />
        )}
      </SectionBody>
    </Section>
  )
}
